import { QUESTIONNAIRE_TEXT_VERSION } from '@360face/domain';
import type {
  ClientMembership,
  AssistMirror,
  BookingGiftOption,
  KioskAssistCall,
  CheckInIdentity,
  GiftCertificateOption,
  Locale,
  KioskVisitView,
  PaymentMethod,
  ProductOffer,
  QuestionnaireDefinition,
  QuestionnaireFormRequest,
  RecognizedCustomerView,
  Service,
  Slot,
  Staff,
} from '@360face/domain';

export type KioskVisit = KioskVisitView & { kioskToken: string };

export interface CheckInMatch {
  ref: string;
  start: string;
  titles: string[];
  staffId?: string;
  firstName: string;
}

export interface CreateVisitInput {
  idempotencyKey: string;
  serviceIds: string[];
  addonIds: string[];
  productIds: string[];
  staffId: string;
  start: string;
  /** Full details, or the token of a recognised returning client (e-mail only if none on file). */
  customer: { fullName: string; phone: string; email: string } | { recognized: string; phone: string; email?: string };
  consents: { privacy: true; marketing: boolean };
  /** Gift for the booking amount chosen by the customer. */
  giftId?: string;
}

/**
 * The only way the frontend talks to the outside world: Hub's canonical API.
 * It never calls a CRM directly and never holds CRM tokens.
 */
export interface HubApi {
  services(locationId: string): Promise<Service[]>;
  staff(locationId: string): Promise<Staff[]>;
  dates(locationId: string, serviceIds: string[], staffId?: string): Promise<string[]>;
  slots(locationId: string, serviceIds: string[], date: string, staffId?: string): Promise<Slot[]>;
  createVisit(locationId: string, input: CreateVisitInput): Promise<KioskVisit>;
  /** Today's bookings of the customer identified by mobile number or loyalty card QR. */
  lookupBookings(locationId: string, who: CheckInIdentity): Promise<CheckInMatch[]>;
  checkIn(locationId: string, ref: string, who: CheckInIdentity): Promise<KioskVisit>;
  /** Memberships still in use of a client with a booking today (kiosk v3, owner 23.10.2026). */
  checkInMemberships?(locationId: string, who: CheckInIdentity): Promise<ClientMembership[]>;
  /** Phone page: today's bookings for a mobile number, and "yes, it's me" (no arrival). */
  prepareLookup(locationId: string, phone: string): Promise<CheckInMatch[]>;
  prepare(locationId: string, ref: string, phone: string): Promise<KioskVisit>;
  /** Details step: is this phone a returning client? (short name + masked e-mail only) */
  recognize(locationId: string, phone: string): Promise<RecognizedCustomerView>;
  addAddons(visit: KioskVisit, addonIds: string[]): Promise<KioskVisit>;
  /** Products for home care in stock, with final (promotion) prices from the server. */
  products(locationId: string): Promise<ProductOffer[]>;
  addProducts(visit: KioskVisit, productIds: string[]): Promise<KioskVisit>;
  giftOptions(locationId: string): Promise<GiftCertificateOption[]>;
  buyGift(
    locationId: string,
    idempotencyKey: string,
    giftId: string,
    phone: string,
    delivery: 'pickup' | 'email',
    email?: string,
    card?: { to: string; from: string; message?: string },
  ): Promise<KioskVisit>;
  /** Questionnaire answer only (never the answers to the form itself). */
  questionnaire(visit: KioskVisit, answer: 'confirmed' | 'needed'): Promise<KioskVisit>;
  classPassToday(locationId: string): Promise<Array<{ ref: string; start: string }>>;
  classPassIdentify(locationId: string, ref: string): Promise<{ fullName: string }>;
  classPassCheckIn(locationId: string, ref: string): Promise<KioskVisit>;
  freeMinutes(visit: KioskVisit): Promise<{ freeMinutes: number | null }>;
  kioskConfig(locationId: string): Promise<{ welcomeLanguages: Locale[]; bookingGifts: BookingGiftOption[] }>;
  /** Gift for the booking amount, chosen at check-in. */
  chooseGift(visit: KioskVisit, giftId: string): Promise<KioskVisit>;
  /** New booking: free minutes after a slot of `durationMin` before the therapist's next booking. */
  slotRoom(
    locationId: string,
    staffId: string,
    start: string,
    durationMin: number,
  ): Promise<{ freeMinutes: number | null }>;
  /** Health questionnaire for the location (questions + consent text, all kiosk languages). */
  questionnaireDefinition(locationId: string): Promise<QuestionnaireDefinition>;
  /** Sends the filled-in form; the server stores it encrypted. The kiosk keeps nothing afterwards. */
  submitQuestionnaire(visit: KioskVisit, form: QuestionnaireFormRequest): Promise<KioskVisit>;
  /** "Call staff": a banner on the therapist tablets. `screen` = kiosk step, no personal data. */
  callStaff(locationId: string, screen: string): Promise<void>;
  /** Video call with the operator (owner 20.10.2026). The answer carries the call's secret. */
  assistStart(locationId: string, screen: string, locale: Locale): Promise<KioskAssistCall>;
  assistGet(locationId: string, id: string, secret: string): Promise<KioskAssistCall>;
  /** What the operator sees of the screen; dropped by the server unless the call is connected. */
  assistMirror(locationId: string, id: string, secret: string, mirror: AssistMirror): Promise<void>;
  assistEnd(locationId: string, id: string, secret: string): Promise<KioskAssistCall>;
  selectPaymentMethod(visit: KioskVisit, method: PaymentMethod): Promise<KioskVisit>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly retryable: boolean,
    /** Text from another system (e.g. the accounting system's refusal), shown small under our text. */
    readonly detail?: string,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

/**
 * Test mode of the kiosk (owner 21.10.2026): `?test=1` once on a device shows the test therapist
 * ("Mila · TESTE Hub"); `?test=0` hides it again. Every other kiosk never offers the test therapist.
 */
export function kioskTestMode(): boolean {
  try {
    const flag = new URLSearchParams(window.location.search).get('test');
    if (flag === '1') window.localStorage.setItem('hub-test', '1');
    if (flag === '0') window.localStorage.removeItem('hub-test');
    return window.localStorage.getItem('hub-test') === '1';
  } catch {
    return false;
  }
}

/** Hides test therapists (and their services and slots) unless the kiosk is in test mode. */
export function withoutTestStaff(api: HubApi, testMode: boolean): HubApi {
  if (testMode) return api;
  const testIds = async (loc: string) =>
    new Set((await api.staff(loc).catch(() => [] as Staff[])).filter((s) => s.test).map((s) => s.id));
  return {
    ...api,
    staff: async (loc) => (await api.staff(loc)).filter((s) => !s.test),
    services: async (loc) => {
      const [list, hidden] = await Promise.all([api.services(loc), testIds(loc)]);
      if (hidden.size === 0) return list;
      return list
        .map((s) => ({ ...s, staffIds: s.staffIds.filter((id) => !hidden.has(id)) }))
        .filter((s) => s.staffIds.length > 0);
    },
    slots: async (loc, serviceIds, date, staffId) => {
      const [list, hidden] = await Promise.all([api.slots(loc, serviceIds, date, staffId), testIds(loc)]);
      return list.filter((s) => !hidden.has(s.staffId));
    },
  };
}

export function createHubApi(baseUrl = '', fetchImpl: typeof fetch = (...a) => fetch(...a)): HubApi {
  async function call<T>(
    method: 'GET' | 'POST',
    path: string,
    opts: { query?: Record<string, string | undefined>; body?: unknown; headers?: Record<string, string> } = {},
  ): Promise<T> {
    const qs = Object.entries(opts.query ?? {})
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`)
      .join('&');
    let res: Response;
    try {
      res = await fetchImpl(`${baseUrl}${path}${qs ? `?${qs}` : ''}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...opts.headers,
        },
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        signal: AbortSignal.timeout(20_000),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', true);
    }
    const body = (await res.json().catch(() => ({}))) as {
      data?: T;
      error?: { code?: string; retryable?: boolean };
      message?: { error?: { code?: string } };
    };
    if (!res.ok) {
      const code = body.error?.code ?? body.message?.error?.code ?? 'UNKNOWN';
      throw new ApiError(res.status, code, body.error?.retryable ?? res.status >= 500);
    }
    return body.data as T;
  }

  const visitHeaders = (v: KioskVisit) => ({ 'X-Kiosk-Token': v.kioskToken });

  return {
    services: (loc) => call('GET', `/v1/locations/${loc}/services`),
    staff: (loc) => call('GET', `/v1/locations/${loc}/staff`),
    dates: (loc, serviceIds, staffId) =>
      call('GET', `/v1/locations/${loc}/availability/dates`, { query: { serviceIds: serviceIds.join(','), staffId } }),
    slots: (loc, serviceIds, date, staffId) =>
      call('GET', `/v1/locations/${loc}/availability/slots`, {
        query: { serviceIds: serviceIds.join(','), date, staffId },
      }),
    createVisit: (loc, input) => call('POST', `/v1/locations/${loc}/visits`, { body: input }),
    lookupBookings: (loc, who) => call('POST', `/v1/locations/${loc}/check-in/lookup`, { body: who }),
    checkInMemberships: (loc, who) => call('POST', `/v1/locations/${loc}/check-in/memberships`, { body: who }),
    checkIn: (loc, ref, who) => call('POST', `/v1/locations/${loc}/check-in`, { body: { ref, ...who } }),
    prepareLookup: (loc, phone) => call('POST', `/v1/locations/${loc}/prepare/lookup`, { body: { phone } }),
    prepare: (loc, ref, phone) => call('POST', `/v1/locations/${loc}/prepare`, { body: { ref, phone } }),
    recognize: (loc, phone) => call('POST', `/v1/locations/${loc}/customers/recognize`, { body: { phone } }),
    addAddons: async (v, addonIds) => ({
      ...(await call<KioskVisit>('POST', `/v1/visits/${v.id}/addons`, {
        body: { addonIds },
        headers: visitHeaders(v),
      })),
      kioskToken: v.kioskToken,
    }),
    products: (loc) => call('GET', `/v1/locations/${loc}/products`),
    addProducts: async (v, productIds) => ({
      ...(await call<KioskVisit>('POST', `/v1/visits/${v.id}/products`, {
        body: { productIds },
        headers: visitHeaders(v),
      })),
      kioskToken: v.kioskToken,
    }),
    giftOptions: (loc) => call('GET', `/v1/locations/${loc}/gift-certificates`),
    buyGift: (loc, idempotencyKey, giftId, phone, delivery, email, card) =>
      call('POST', `/v1/locations/${loc}/gift-sales`, {
        body: { idempotencyKey, giftId, phone, delivery, email, card },
      }),
    questionnaire: async (v, answer) => ({
      ...(await call<KioskVisit>('POST', `/v1/visits/${v.id}/questionnaire`, {
        body: { answer, textVersion: QUESTIONNAIRE_TEXT_VERSION },
        headers: visitHeaders(v),
      })),
      kioskToken: v.kioskToken,
    }),
    classPassToday: (loc) => call('GET', `/v1/locations/${loc}/classpass/today`),
    classPassIdentify: (loc, ref) => call('POST', `/v1/locations/${loc}/classpass/identify`, { body: { ref } }),
    classPassCheckIn: (loc, ref) => call('POST', `/v1/locations/${loc}/classpass/check-in`, { body: { ref } }),
    slotRoom: (loc, staffId, start, durationMin) =>
      call('GET', `/v1/locations/${loc}/availability/free-minutes`, {
        query: { staffId, start, durationMin: String(durationMin) },
      }),
    kioskConfig: (loc) => call('GET', `/v1/locations/${loc}/kiosk-config`),
    chooseGift: async (v, giftId) => ({
      ...(await call<KioskVisit>('POST', `/v1/visits/${v.id}/gift`, { body: { giftId }, headers: visitHeaders(v) })),
      kioskToken: v.kioskToken,
    }),
    freeMinutes: (v) => call('GET', `/v1/visits/${v.id}/free-minutes`, { headers: visitHeaders(v) }),
    questionnaireDefinition: (loc) => call('GET', `/v1/locations/${loc}/questionnaire`),
    submitQuestionnaire: async (v, form) => ({
      ...(await call<KioskVisit>('POST', `/v1/visits/${encodeURIComponent(v.id)}/questionnaire/form`, {
        body: form,
        headers: visitHeaders(v),
      })),
      kioskToken: v.kioskToken,
    }),
    callStaff: async (loc, screen) => {
      await call('POST', `/v1/locations/${loc}/assist`, { body: { screen } });
    },
    assistStart: (loc, screen, locale) =>
      call('POST', `/v1/locations/${loc}/assist/calls`, { body: { screen, locale } }),
    assistGet: (loc, id, secret) =>
      call('GET', `/v1/locations/${loc}/assist/calls/${encodeURIComponent(id)}`, {
        headers: { 'X-Assist-Secret': secret },
      }),
    assistMirror: async (loc, id, secret, mirror) => {
      await call('POST', `/v1/locations/${loc}/assist/calls/${encodeURIComponent(id)}/mirror`, {
        body: mirror,
        headers: { 'X-Assist-Secret': secret },
      });
    },
    assistEnd: (loc, id, secret) =>
      call('POST', `/v1/locations/${loc}/assist/calls/${encodeURIComponent(id)}/end`, {
        headers: { 'X-Assist-Secret': secret },
      }),
    selectPaymentMethod: async (v, method) => ({
      ...(await call<KioskVisit>('POST', `/v1/visits/${v.id}/payment-method`, {
        body: { method },
        headers: visitHeaders(v),
      })),
      kioskToken: v.kioskToken,
    }),
  };
}
